import {
  selectBuildingsDamage,
  selectHexes,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectUnits,
} from "../redux/reducers/sharedStateReducer/selectors";
import {calculateCitiesCache} from "../utils/gameState/calculateCitiesCache";
import {getCityGrowthAnimationTarget} from "../utils/gameState/city/getCityGrowthAnimationTarget";
import {selectRuinsIncomeRoundLimit} from "../utils/gameState/income/checkIfRoundWithIncreasedRuinsIncome";
import {CENTRAL_QUARTER_PREFIX, FLAG_PREFIX, KEY_POINT_TOKENS} from "../constants/tokens";
import {AFFECTED_BUILDING_REVEAL_STAGGER_MS} from "./playNearbyGuildsTemplesIncomeChangeAnimation";
import {checkIfStartingQuarterJustPlaced} from "./playBuildingDustAnimation";

// Задержка проигрывания — та же, что у соседних разовых анимаций постройки
// (NEARBY_INCOME_CHANGE_DELAY_MS, setTimeout вокруг
// showSpecialBuildingIncomeAnimation): без нее заливка города начинает
// проявляться в том же кадре, в котором игрок поставил жетон, и накрывает его
// (оверлей на LAYER_DEPTH.CITY_OWNERSHIP выше жетонов на LAYER_DEPTH.TOKEN)
// прежде, чем игрок успевает разглядеть саму постройку
const CITY_GROWTH_ANIMATION_DELAY_MS = 1000;

// Рудники и руины звучат по-разному; внутри вида великий звучит вместо
// обычного: [обычный, великий]
const KEY_POINT_SOUND_KINDS = [
  [KEY_POINT_TOKENS.MINE, KEY_POINT_TOKENS.SUPER_MINE],
  [KEY_POINT_TOKENS.RUINS, KEY_POINT_TOKENS.SUPER_RUINS],
];

/**
 * Какие звуки захвата сыграть вместе с доходом присоединенных ключевых точек:
 * по одному на рудники и на руины — великой точки, если она среди них есть.
 * Два одинаковых звука разом не нужны: второй оборвал бы первый (SoundManager
 * держит один экземпляр на звук).
 *
 * Рудник на самой ячейке постройки молчит: это разведка рудника гномами, и ее
 * звук уже прозвучал при постройке. В план рудник не ставится — там этого
 * случая нет.
 *
 * @param {Array<{hexKey: string, tokenType: string}>} keyPoints
 * @param {string} placedHexKey
 * @returns {string[]} жетоны, по которым звучит SoundManager.playKeyPointCapture
 */
export const getKeyPointCaptureSoundTokens = (keyPoints, placedHexKey) => {
  const tokens = new Set(
    keyPoints.filter(({hexKey}) => hexKey !== placedHexKey).map(({tokenType}) => tokenType)
  );

  return KEY_POINT_SOUND_KINDS
    .map(([token, superToken]) => (tokens.has(superToken) ? superToken : tokens.has(token) ? token : null))
    .filter(Boolean);
};

/**
 * После постройки обычного строения города (базовый квартал, ферма, казарма,
 * рудник) или центрального квартала показывает, ЧТО именно у игрока прибавилось:
 * оверлеем цвета владельца заливается весь город, которому досталась ячейка, и
 * поверх него проигрывается доход появившихся у города рудников и руин — со
 * звуком их захвата (см. getKeyPointCaptureSoundTokens).
 *
 * hexesBefore нужен нетронутым, каким он был ДО диспатча размещения: по нему
 * считается состав города «до», чтобы отличить только что присоединенные
 * ключевые точки от давно принадлежавших городу.
 *
 * Как и в playNearbyGuildsTemplesIncomeChangeAnimation, состояние читается
 * СИНХРОННО сразу по возврату из dispatch — store.getState() здесь гарантированно
 * отражает именно это размещение и ничего позже него; отложен только момент
 * ПРОИГРЫВАНИЯ. Читать состояние внутри отложенного колбэка нельзя: игрок
 * успевает поставить второе строение за секунду задержки, и колбэк первого
 * увидел бы в сторе уже оба.
 *
 * @param store
 * @param hexGridRenderer
 * @param placedHexKey
 * @param hexesBefore
 * @param {Object} [citiesCaches] - посчитанная вызывающим кодом пара кэшей
 *   городов «до/после» (см. playBuildingPlacementEffects); без нее считается
 *   здесь же
 * @param {Object} [soundManager] - без него рост города играет без звука
 */
export const playCityGrowthAnimation = (
  store, hexGridRenderer, placedHexKey, hexesBefore, citiesCaches, soundManager
) => {
  if (!hexGridRenderer || !hexesBefore) return;

  const state = store.getState();
  const hexesAfter = selectHexes(state);
  const specialBuildings = selectSpecialBuildings(state);

  const citiesCacheBefore = citiesCaches
    ? citiesCaches.citiesCacheBefore : calculateCitiesCache(hexesBefore, specialBuildings);
  const citiesCacheAfter = citiesCaches
    ? citiesCaches.citiesCacheAfter : calculateCitiesCache(hexesAfter, specialBuildings);

  const target = getCityGrowthAnimationTarget(
    placedHexKey, hexesAfter[placedHexKey], citiesCacheBefore, citiesCacheAfter, hexesAfter
  );
  if (!target) return;

  scheduleCityGrowthAnimation(state, hexGridRenderer, soundManager, placedHexKey, target, {
    citiesCache: citiesCacheAfter,
    hexes: hexesAfter,
    specialBuildings,
  });
};

/**
 * Рост города при выборе стартового квартала в фазе расстановки — как после
 * постройки центрального квартала: город основан, и все его рудники и руины
 * показывают доход со звуком захвата.
 *
 * В hexes такого квартала до конца расстановки нет (там флаг, выбор лежит в
 * startingQuarters), поэтому город считается на поле «как после расстановки»,
 * так же, как его строит редьюсер в конце фазы: флаги сняты, на ячейке —
 * центральный квартал игрока. Чужие стартовые кварталы скрыты до конца
 * расстановки и в расчет не идут.
 *
 * Выбор можно сменить до показа (клик по другому флагу): тогда показывать
 * нечего.
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} [soundManager] - без него рост города играет без звука
 * @param {string} hexKey
 * @param {Object} params
 * @param {string} params.playerId — чей стартовый квартал
 * @param {string} params.color — цвет игрока: от него зависит жетон квартала
 */
export const playStartingQuarterCityGrowthAnimation = (store, hexGridRenderer, soundManager, hexKey, {
  playerId,
  color,
}) => {
  if (!hexGridRenderer || !checkIfStartingQuarterJustPlaced(store, hexKey, playerId)) return;

  const state = store.getState();
  const specialBuildings = selectSpecialBuildings(state);
  const centralQuarterToken = `${CENTRAL_QUARTER_PREFIX}${color}`;
  const hexes = Object.fromEntries(
    Object.entries(selectHexes(state)).filter(([, token]) => !String(token).startsWith(FLAG_PREFIX))
  );
  hexes[hexKey] = centralQuarterToken;
  const citiesCache = calculateCitiesCache(hexes, specialBuildings);

  // Центральному кварталу состав города «до» не нужен: новым считается весь город
  const target = getCityGrowthAnimationTarget(hexKey, centralQuarterToken, null, citiesCache, hexes);
  if (!target) return;

  scheduleCityGrowthAnimation(
    state, hexGridRenderer, soundManager, hexKey, target, {citiesCache, hexes, specialBuildings},
    () => checkIfStartingQuarterJustPlaced(store, hexKey, playerId)
  );
};

/**
 * Отложенный показ уже посчитанного роста города (target — см.
 * getCityGrowthAnimationTarget): оверлей и доход ключевых точек через
 * CITY_GROWTH_ANIMATION_DELAY_MS и тогда же звуки их захвата. Общий для
 * настоящей постройки и постановки в план (playPlanPlacementIncomeAnimations).
 *
 * Данные для дохода берутся из state и field в момент вызова — по ним и
 * посчитан target; отложен только показ.
 *
 * @param {Object} state
 * @param {Object} hexGridRenderer
 * @param {Object} [soundManager] - без него рост города играет без звука
 * @param {string} placedHexKey
 * @param {Object} target
 * @param {{citiesCache: Object, hexes: Object, specialBuildings: Object}} field -
 *   поле, на котором посчитан target: настоящее или «с планом»
 * @param {Function} [checkIfStillShown] - к показу постройку могли снять
 *   (запланированное снимается): тогда ни анимации, ни звука
 */
export const scheduleCityGrowthAnimation = (
  state, hexGridRenderer, soundManager, placedHexKey, target, {citiesCache, hexes, specialBuildings},
  checkIfStillShown = () => true
) => {
  const animationParams = {
    playerColor: target.playerColor,
    cityHexKeys: target.cityHexKeys,
    keyPoints: target.keyPoints,
    incomeContext: {
      players: selectPlayers(state),
      citiesCache,
      hexes,
      units: selectUnits(state),
      round: selectRound(state),
      ruinsIncomeRoundLimit: selectRuinsIncomeRoundLimit(state),
      specialBuildings,
      buildingsDamage: selectBuildingsDamage(state),
    },
  };

  setTimeout(() => {
    if (checkIfStillShown()) {
      hexGridRenderer.showCityGrowthAnimation(animationParams);
    }
  }, CITY_GROWTH_ANIMATION_DELAY_MS);

  // Рудник и руины, полученные разом, звучат внахлест с тем же сдвигом, что и
  // доход нескольких затронутых гильдий и храмов, — чтобы оставаться различимыми
  const captureSoundTokens = soundManager ? getKeyPointCaptureSoundTokens(target.keyPoints, placedHexKey) : [];
  captureSoundTokens.forEach((token, index) => {
    setTimeout(() => {
      if (checkIfStillShown()) {
        soundManager.playKeyPointCapture(token);
      }
    }, CITY_GROWTH_ANIMATION_DELAY_MS + index * AFFECTED_BUILDING_REVEAL_STAGGER_MS);
  });
};
